import React, { useReducer, useEffect } from 'react';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';

import { fg } from '@confluence/feature-gating';

import { useStaticMacroStoreContext } from './StaticMacroStoreContext.tsx';

const spinnerWrapperStyles = css({ overflow: 'hidden' });

type ForgeMacroProps = {
	localId?: string;
	extensionKey?: string;
	guestParams?: unknown;
	renderIframe: () => React.ReactElement;
	isEditing?: boolean;
	isLivePage?: boolean;
};

export function createForgeMacroExtensionHandler(
	isEditing?: boolean,
	isLivePage?: boolean,
):
	| ((node: any, renderFn: (param: { references: any[] }) => JSX.Element) => JSX.Element)
	| undefined {
	if (!fg('conf_frontend_forge_static_macro_mvp')) {
		return undefined;
	}
	return (node: any, renderFn: (param: { references: any[] }) => JSX.Element): JSX.Element => (
		<ForgeMacro
			localId={node?.parameters?.localId}
			extensionKey={node?.extensionKey?.split('/').at(-1)}
			guestParams={node?.parameters?.guestParams}
			renderIframe={() => renderFn({ references: [] })}
			isEditing={isEditing}
			isLivePage={isLivePage}
		/>
	);
}

export function ForgeMacro({
	localId,
	extensionKey,
	guestParams,
	renderIframe,
	isEditing,
	isLivePage,
}: ForgeMacroProps): React.ReactElement {
	const ctx = useStaticMacroStoreContext();
	const store = ctx?.storeRef.current;
	const [, forceUpdate] = useReducer((n: number) => n + 1, 0);
	const readyEventEmittedRef = React.useRef(false);

	// Check if this extension supports staticView, based on preloaded extension data.
	// extensionsLoaded is true when __SSR_FORGE_EXTENSIONS__ has been populated.
	// isStatic may be false either because (a) extensions not loaded yet, or (b) not a static macro.
	// We only use isStatic as a fallback signal when we know extensions ARE loaded.
	const isStatic = ctx && extensionKey ? ctx.isStaticExtension(extensionKey) : false;
	const extensionsLoaded = !!window.__SSR_FORGE_EXTENSIONS__?.['xen:macro']?.extensions?.length;

	useEffect(() => {
		if (!ctx || !localId) return;
		const unsubscribe = ctx.subscribe(localId, forceUpdate);
		// Data may have already arrived before we subscribed — check immediately
		if (store?.html.has(localId) || store?.errors.has(localId) || store?.fullyLoaded) {
			forceUpdate();
		}
		return unsubscribe;
	}, [ctx, localId, forceUpdate, store]);

	useEffect(() => {
		if (!ctx || !localId || !isStatic) {
			return undefined;
		}
		if (!isEditing || !isLivePage) {
			return undefined;
		}
		if (!ctx.hasMountedBefore(localId)) {
			ctx.markMounted(localId);
			return undefined;
		}
		ctx.triggerRefetch(localId);
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const guestParamsRef = React.useRef<string | undefined>(undefined);
	const serializedGuestParams = React.useMemo(
		() => JSON.stringify(guestParams ?? null),
		[guestParams],
	);

	const html = localId ? store?.html.get(localId) : undefined;
	const errorCode = localId ? store?.errors.get(localId) : undefined;

	// Signal cc-puppeteer that this static macro has finished rendering.
	// cc-puppeteer's waitForAllExtensionReadyEvents() waits for EXTENSION_READY from every
	// Forge extension it finds in the ADF (regardless of emitsReadyEvent manifest declaration).
	// Without this signal, the full 10s timeout is consumed on every PDF export with a static macro.
	// Guarded by a ref so it only fires once per ForgeMacro instance.
	useEffect(() => {
		if (html && localId && !readyEventEmittedRef.current) {
			readyEventEmittedRef.current = true;
			window.postMessage(
				{ source: 'forge-extension', type: 'EXTENSION_READY', localId },
				window.location.origin,
			);
		}
	}, [html, localId]);

	// When a config changes, we want to trigger a re-render.
	useEffect(() => {
		if (!ctx || !localId || !isStatic || !isEditing) return undefined;
		const serialized = serializedGuestParams;
		const prev = guestParamsRef.current;
		guestParamsRef.current = serialized;
		// Skip on first render for this macro instance — only refetch on subsequent changes.
		if (prev === undefined || prev === serialized) return undefined;
		ctx.triggerRefetch(localId);
		return undefined;
	}, [ctx, localId, isStatic, isEditing, serializedGuestParams]);

	if (!ctx || !localId || !store) {
		return renderIframe();
	}

	if (html) {
		return (
			<div
				data-testid="forge-static-macro-content"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	if (errorCode) {
		return renderIframe();
	}

	// Once the query is fully loaded, any macro without html/error is not a static macro → iframe.
	// Also fall back immediately if we KNOW this extension doesn't support staticView —
	// only treat !isStatic as definitive when extensions have actually been loaded.
	// If extensions aren't loaded yet, show spinner and wait (preloader may still be in-flight).
	if (store.fullyLoaded || (extensionsLoaded && !isStatic)) {
		return renderIframe();
	}

	// Extension supports staticView but data hasn't arrived yet — show spinner briefly.
	// Use the same testid as the iframe loader so cc-puppeteer waits for it to disappear.
	return (
		<div css={spinnerWrapperStyles} data-testid="forge-app-loading-spinner">
			<Spinner size="small" />
		</div>
	);
}
